import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { useToast } from "../hooks/use-toast";
import { productCategories, companyInfo } from "../data/mock";
import { Send, FileText, CheckCircle2 } from "lucide-react";

const RequestInfo = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    productType: "",
    quantity: "",
    timeline: "",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: companyInfo.web3formsKey,
          subject: `Quote request from ${form.name}${form.company ? " (" + form.company + ")" : ""}`,
          from_name: "D-H Gadiola Website",
          name: form.name,
          company: form.company,
          email: form.email,
          phone: form.phone,
          product_type: form.productType,
          quantity: form.quantity,
          timeline: form.timeline,
          details: form.details,
          replyto: form.email,
        }),
      });
      const data = await res.json();
      if (data && data.success) {
        toast({
          title: "Request submitted",
          description: "Our team will reach out within 1 business day.",
        });
        setSubmitted(true);
      } else {
        throw new Error(data?.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      toast({
        title: "Request not sent",
        description: err.message || "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({
      name: "",
      company: "",
      email: "",
      phone: "",
      productType: "",
      quantity: "",
      timeline: "",
      details: "",
    });
    setSubmitted(false);
  };

  return (
    <PageLayout>
      <PageHero
        badge="Request Info"
        title="Request a quote or product info"
        subtitle="Send us your project specs and we'll prepare a detailed quote with pricing, lead time, and recommended alternatives."
        breadcrumbs={[{ label: "Request Info" }]}
      />

      <section className="py-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1100px] mx-auto px-6 grid lg:grid-cols-[1.1fr_2fr] gap-8">
          {/* Side panel */}
          <div className="space-y-6">
            <div className="bg-white rounded-3xl p-7 border border-slate-100">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 shadow-lg shadow-emerald-500/30"
                style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
              >
                <FileText className="text-white" size={24} />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: "#0a2e1f" }}>What to include</h3>
              <p className="text-sm text-slate-600 leading-relaxed mb-4">
                The more detail we have, the more accurate the quote. A typical request includes:
              </p>
              <ul className="space-y-2.5">
                {[
                  "Product type or part number",
                  "Quantity & length needed",
                  "Operating environment",
                  "Required certifications",
                  "Target delivery date",
                ].map((it, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 size={16} className="text-emerald-600 mt-0.5 shrink-0" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="rounded-3xl p-7 text-white"
              style={{ background: "linear-gradient(135deg, #0a2e1f 0%, #047857 100%)" }}
            >
              <h3 className="text-lg font-bold mb-2">Need it fast?</h3>
              <p className="text-sm text-emerald-100/90 leading-relaxed mb-4">
                For urgent requests, message us directly on WhatsApp.
              </p>
              <a
                href={companyInfo.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-emerald-800 text-sm font-semibold hover:scale-[1.03] transition-transform"
              >
                {companyInfo.whatsapp}
              </a>
            </div>
          </div>

          {/* Form */}
          {submitted ? (
            <div className="bg-white rounded-3xl p-10 md:p-14 border border-slate-100 text-center">
              <div
                className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center shadow-lg shadow-emerald-500/30"
                style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
              >
                <CheckCircle2 className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: "#0a2e1f" }}>
                Thanks for your request!
              </h3>
              <p className="text-slate-600 max-w-md mx-auto mb-6 leading-relaxed">
                Our team has received your request and will get back to you within 1 business day with a detailed quote and lead time.
              </p>
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full text-white text-sm font-semibold"
                style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
              >
                Submit another request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-10 border border-slate-100">
              <h2 className="text-2xl font-bold mb-6" style={{ color: "#0a2e1f" }}>Project Details</h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {[
                  { name: "name", label: "Full Name", required: true },
                  { name: "company", label: "Company" },
                  { name: "email", label: "Email", type: "email", required: true },
                  { name: "phone", label: "Phone" },
                ].map((f) => (
                  <div key={f.name}>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}{f.required && " *"}</label>
                    <input
                      type={f.type || "text"}
                      name={f.name}
                      required={f.required}
                      value={form[f.name]}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                    />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Product Type</label>
                  <select
                    name="productType"
                    value={form.productType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 bg-white"
                  >
                    <option value="">Select category…</option>
                    {productCategories.map((p) => (
                      <option key={p.slug} value={p.title}>{p.title}</option>
                    ))}
                    <option value="Other">Other / Not sure</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Quantity</label>
                  <input
                    name="quantity"
                    value={form.quantity}
                    onChange={handleChange}
                    placeholder="e.g. 500 ft, 200 pcs"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Target Timeline</label>
                  <select
                    name="timeline"
                    value={form.timeline}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 bg-white"
                  >
                    <option value="">Select…</option>
                    <option>Within 1 week</option>
                    <option>1–3 weeks</option>
                    <option>1–2 months</option>
                    <option>3+ months</option>
                    <option>Just exploring</option>
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Project Details</label>
                <textarea
                  name="details"
                  rows={5}
                  value={form.details}
                  onChange={handleChange}
                  placeholder="Tell us about specs, environment, certifications needed…"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 text-white text-sm font-semibold rounded-full shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.02] transition-all disabled:opacity-60"
                style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
              >
                {submitting ? "Submitting..." : (<><Send size={15} /> Submit Request</>)}
              </button>
            </form>
          )}
        </div>
      </section>

      <ContactCTA />
    </PageLayout>
  );
};

export default RequestInfo;
